// Minimal HTML → structured blocks parser for letter export.
// Handles paragraphs, headings (h1-h3), lists (ul/ol), bold/italic spans.

export type InlineRun = { text: string; bold?: boolean; italic?: boolean };
export type Block =
  | { type: "heading"; level: 1 | 2 | 3; runs: InlineRun[] }
  | { type: "paragraph"; runs: InlineRun[] }
  | { type: "list"; ordered: boolean; items: InlineRun[][] };

export function htmlToBlocks(html: string): Block[] {
  if (!html) return [];
  const wrapper = document.createElement("div");
  wrapper.innerHTML = html;
  const blocks: Block[] = [];

  const inlineRuns = (node: Node, bold = false, italic = false): InlineRun[] => {
    if (node.nodeType === Node.TEXT_NODE) {
      const t = node.textContent ?? "";
      return t ? [{ text: t, bold, italic }] : [];
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return [];
    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();
    const nb = bold || tag === "b" || tag === "strong";
    const ni = italic || tag === "i" || tag === "em";
    if (tag === "br") return [{ text: "\n", bold, italic }];
    const out: InlineRun[] = [];
    el.childNodes.forEach((child) => out.push(...inlineRuns(child, nb, ni)));
    return out;
  };

  const walk = (el: Element) => {
    const tag = el.tagName.toLowerCase();
    if (tag === "h1" || tag === "h2" || tag === "h3") {
      blocks.push({ type: "heading", level: Number(tag[1]) as 1 | 2 | 3, runs: inlineRuns(el) });
    } else if (tag === "ul" || tag === "ol") {
      const items: InlineRun[][] = [];
      el.querySelectorAll(":scope > li").forEach((li) => items.push(inlineRuns(li)));
      blocks.push({ type: "list", ordered: tag === "ol", items });
    } else if (tag === "p" || tag === "div") {
      const runs = inlineRuns(el);
      if (runs.length) blocks.push({ type: "paragraph", runs });
    } else {
      // Fallback: traverse children
      Array.from(el.children).forEach(walk);
    }
  };

  Array.from(wrapper.children).forEach(walk);
  // If parser produced nothing (plain text), fall back to a single paragraph
  if (blocks.length === 0 && wrapper.textContent?.trim()) {
    blocks.push({ type: "paragraph", runs: [{ text: wrapper.textContent }] });
  }
  return blocks;
}

export async function loadLogoDataUrl(url: string | null | undefined): Promise<{ dataUrl: string; width: number; height: number } | null> {
  if (!url) return null;
  try {
    const img = new Image();
    img.crossOrigin = "anonymous";
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject();
      img.src = url;
    });
    const canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;
    canvas.getContext("2d")?.drawImage(img, 0, 0);
    return { dataUrl: canvas.toDataURL("image/png"), width: img.width, height: img.height };
  } catch {
    return null;
  }
}

export function funderSlug(funder: string) {
  return funder.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "grant";
}
